import { useState, useEffect } from "react";
import api from "../../axiosconfig";

/**
 * Hook to fetch platform assessment averages with filters
 * 
 * @param {string} viewMode - "current" or "range"
 * @param {object} filters - Filter object { age, gender, location, description, jobTitle, company, interest }
 * @param {string} startDate - ISO date string (YYYY-MM-DD)
 * @param {string} endDate - ISO date string (YYYY-MM-DD)
 * 
 * @returns {object} {
 *   metrics: { ... } - Backend computed metrics,
 *   data: [...] - Individual assessment records,
 *   loading: boolean,
 *   error: string | null,
 *   hasData: boolean
 * }
 */
export default function useAssessmentAverage(viewMode, filters, startDate, endDate) {
    const [metrics, setMetrics] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Only fetch in current mode
        if (viewMode !== "current") return;
        
        // Only fetch when we have date range
        if (!startDate || !endDate) return;

        const fetchAverages = async () => {
            try {
                setLoading(true);
                setError(null);

                // Build query params
                const params = {
                    range_start_month: startDate,
                    range_end_month: endDate,
                };

                // Add filters (only if they have values)
                if (filters.age) params.age = filters.age;
                if (filters.gender) params.gender = filters.gender;
                if (filters.location) params.location = filters.location;
                if (filters.description) params.description = filters.description;
                if (filters.jobTitle) params.jobTitle = filters.jobTitle;
                if (filters.company) params.company = filters.company;
                if (filters.interest) params.interest = filters.interest;

                console.log("[useAssessmentAverage] Fetching with params:", params);

                const response = await api.get('/api/admin/assessment-analytics', { params });

                console.log("[useAssessmentAverage] Response:", response?.data);

                // Backend returns: { status, averages: {...}, metrics: {...}, data: [...], date_range: {...}, filters_applied: {...} }
                const payload = response?.data || {};
                
                // Combine averages and metrics into one object for easier consumption
                const combinedMetrics = {
                    ...(payload.averages || {}),
                    ...(payload.metrics || {})
                };
                
                // Store the full payload for debugging
                console.log("[useAssessmentAverage] Full response structure:", {
                    hasAverages: !!payload.averages,
                    hasMetrics: !!payload.metrics,
                    hasData: !!payload.data,
                    dataLength: payload.data?.length,
                    topLevelKeys: Object.keys(payload),
                    averagesKeys: payload.averages ? Object.keys(payload.averages) : [],
                    metricsKeys: payload.metrics ? Object.keys(payload.metrics) : []
                });
                
                setMetrics(combinedMetrics);
                setData(payload.data || []);

                console.log("[useAssessmentAverage] Set metrics and data:", { combinedMetrics, dataLength: (payload.data || []).length });

            } catch (e) {
                console.error("[useAssessmentAverage] Error fetching data:", e);
                
                // Handle different error scenarios
                if (e.response?.status === 400) {
                    setError("Invalid filter parameters. Please check your filters.");
                } else if (e.response?.status === 204 || !e.response?.data) {
                    // No data available - not an error, just empty
                    setMetrics(null);
                    setData([]);
                    setError(null);
                } else if (!navigator.onLine) {
                    setError("Network error. Please check your connection.");
                } else {
                    setError("Unable to load assessment averages. Please try again.");
                }
                
                setMetrics(null);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAverages();
    }, [viewMode, startDate, endDate, filters.age, filters.gender, filters.location, 
        filters.description, filters.jobTitle, filters.company, filters.interest]);

    // Determine if we have valid data
    // Check if at least one domain score exists and is greater than 0
    const hasData = metrics !== null && Object.entries(metrics).some(([key, value]) => {
        const domainKeys = ['physical', 'emotional', 'acceptance', 'relationships', 'loving_kindness', 'intellectual'];
        return domainKeys.includes(key) && typeof value === 'number' && value > 0;
    });

    return {
        metrics,
        data,
        loading,
        error,
        hasData,
    };
}
